import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getNestedComments, addNestedComment } from '../../../firebase/solutionReasonNestedCommentsApi.js';
import { fetchUsersByIds } from '../../slices/usersCacheSlice.js';

// Helper to convert Firestore timestamps (or JS dates/strings) to millis for sorting
const toMillis = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts?.seconds === 'number') return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6;
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === 'string') return new Date(ts).getTime() || 0;
    if (typeof ts === 'number') return ts;
  } catch {}
  return 0;
};

// Async thunks
export const fetchNestedComments = createAsyncThunk(
  'solutionView/nestedComments/fetch',
  async (parentCommentId, { dispatch }) => {
    const res = await getNestedComments(parentCommentId, { pageSize: 20, fetchPolicy: 'cache-first' });
    try {
      const ids = Array.from(new Set((res?.comments || []).map(c => c.userId).filter(Boolean)));
      if (ids.length > 0) {
        dispatch(fetchUsersByIds({ userIds: ids }));
      }
    } catch (_) {}
    return res;
  }
);

export const postNestedComment = createAsyncThunk(
  'solutionView/nestedComments/add',
  async ({ reasonId, parentCommentId, userId, userName, userProfession, text }) => {
    return await addNestedComment({ reasonId, parentCommentId, userId, userName, userProfession, text });
  }
);

const nestedCommentsSlice = createSlice({
  name: 'solutionViewNestedComments',
  initialState: {
    commentsByParentId: {} // { [parentCommentId]: { comments: [], loading: false, error: null } }
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNestedComments.pending, (state, action) => {
        const parentId = action.meta.arg;
        state.commentsByParentId[parentId] = state.commentsByParentId[parentId] || { comments: [] };
        state.commentsByParentId[parentId].loading = true;
        state.commentsByParentId[parentId].error = null;
      })
      .addCase(fetchNestedComments.fulfilled, (state, action) => {
        const { parentCommentId, comments } = action.payload;
        const sorted = (comments || []).slice().sort((a, b) => toMillis(a.timestamp) - toMillis(b.timestamp));
        state.commentsByParentId[parentCommentId] = {
          comments: sorted,
          loading: false,
          error: null
        };
      })
      .addCase(fetchNestedComments.rejected, (state, action) => {
        const parentId = action.meta.arg;
        state.commentsByParentId[parentId] = state.commentsByParentId[parentId] || { comments: [] };
        state.commentsByParentId[parentId].loading = false;
        state.commentsByParentId[parentId].error = action.error.message;
      })
      // Optimistic insert on pending
      .addCase(postNestedComment.pending, (state, action) => {
        const { parentCommentId, userId, userName, userProfession, text } = action.meta.arg || {};
        const requestId = action.meta.requestId;
        state.commentsByParentId[parentCommentId] = state.commentsByParentId[parentCommentId] || { comments: [] };
        state.commentsByParentId[parentCommentId].comments.push({
          id: `optimistic-${requestId}`,
          clientRequestId: requestId,
          parentCommentId,
          userId,
          userName,
          userProfession,
          text,
          timestamp: new Date(),
          __optimistic: true
        });
      })
      .addCase(postNestedComment.fulfilled, (state, action) => {
        const { parentCommentId, comment } = action.payload;
        const requestId = action.meta.requestId;
        state.commentsByParentId[parentCommentId] = state.commentsByParentId[parentCommentId] || { comments: [] };
        // Replace optimistic comment if present
        const list = state.commentsByParentId[parentCommentId].comments;
        const idx = list.findIndex((c) => c.clientRequestId === requestId || c.__optimistic);
        if (idx !== -1) {
          list[idx] = comment;
        } else {
          list.push(comment);
        }
      })
      .addCase(postNestedComment.rejected, (state, action) => {
        const { parentCommentId } = action.meta.arg || {};
        const requestId = action.meta.requestId;
        if (!parentCommentId) return;
        state.commentsByParentId[parentCommentId] = state.commentsByParentId[parentCommentId] || { comments: [] };
        const list = state.commentsByParentId[parentCommentId].comments;
        const idx = list.findIndex((c) => c.clientRequestId === requestId || c.__optimistic);
        if (idx !== -1) list.splice(idx, 1);
      });
  }
});

export default nestedCommentsSlice.reducer;
